import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import { TransformWrapper } from 'react-zoom-pan-pinch';
import { MediaQuery, Util, IconButton, Modal } from '@grandvisionhq/common';
import { Icon } from '@grandvisionhq/icons';
import { useIntl } from '@grandvisionhq/state';
import { useImageSlider } from '../hooks/use-image-slider.js';
import { ZoomModalImage } from './components/ZoomModalImage/ZoomModalImage.js';

const defaultImageSizes = {
    M: 768,
    L: 1024,
    XL: 1024,
    MAX: 1440
};
const ConditionalWrapper = ({ shouldWrap , wrapper , children  })=>/*#__PURE__*/ React.createElement(React.Fragment, null, shouldWrap ? wrapper(children) : children)
;
const ZoomModal = ({ isOpen , images , onClose , openingIndex  })=>{
    const { getLabel  } = useIntl();
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const extendedImages = React.useMemo(()=>{
        return images.map((image)=>swcHelpers.objectSpread({}, image, {
                srcSet: Util.createImageSrcSet(image.url, defaultImageSizes, {
                    fit: 'fill',
                    format: 'jpeg',
                    aspectRatio: 16 / 9
                }),
                sizes: Util.createImageSizes(defaultImageSizes, image.url)
            })
        );
    }, [
        images
    ]);
    const { setSlide , prevSlide , nextSlide , handleInView , onFirstSlide , onLastSlide , activeIndex , interruptSwitchEffect ,  } = useImageSlider(extendedImages.length, {
        initialIndex: openingIndex || 0,
        elementIdentifierString: 'zoom-modal__image-slide',
        enableKeyEvents: isOpen
    });
    const handlePrevClick = React.useCallback((event)=>{
        event.preventDefault();
        prevSlide();
    }, [
        prevSlide
    ]);
    const handleNextClick = React.useCallback((event)=>{
        event.preventDefault();
        nextSlide();
    }, [
        nextSlide
    ]);
    const handleClose = React.useCallback(()=>{
        interruptSwitchEffect.current = true;
        setTimeout(()=>interruptSwitchEffect.current = false
        , 200);
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }, [
        interruptSwitchEffect,
        onClose
    ]);
    const leftChevron = /*#__PURE__*/ React.createElement(IconButton, {
        className: classnames('zoom-modal__chevron', 'zoom-modal__chevron-left', {
            disabled: onFirstSlide
        }),
        disabled: onFirstSlide,
        onClick: handlePrevClick,
        "aria-label": getLabel('common.actions.previous')
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "chevron-left"
    }));
    const rightChevron = /*#__PURE__*/ React.createElement(IconButton, {
        className: classnames('zoom-modal__chevron', 'zoom-modal__chevron-right', {
            disabled: onLastSlide
        }),
        disabled: onLastSlide,
        onClick: handleNextClick,
        "aria-label": getLabel('common.actions.next')
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "chevron-right"
    }));
    return /*#__PURE__*/ React.createElement(Modal, {
        isOpen: isOpen,
        secondaryIconsSlot: !isSmallScreen && /*#__PURE__*/ React.createElement(React.Fragment, null, leftChevron, rightChevron),
        showIcon: true,
        isFullScreen: true,
        onModalClose: handleClose,
        className: "zoom-modal"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "zoom-modal__image-container"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "zoom-modal__image-wrapper"
    }, /*#__PURE__*/ React.createElement(ConditionalWrapper, {
        shouldWrap: isSmallScreen,
        wrapper: (children)=>/*#__PURE__*/ React.createElement(TransformWrapper, {
                panning: {
                    disabled: true
                }
            }, children)
    }, extendedImages.map((image, index)=>/*#__PURE__*/ React.createElement(ZoomModalImage, {
            key: image.label,
            image: image,
            handleInView: handleInView,
            index: index,
            isSmallScreen: isSmallScreen
        })
    )))), isSmallScreen && leftChevron, /*#__PURE__*/ React.createElement("div", {
        className: "zoom-modal__navigator",
        "data-t": "zoom-modal__navigator"
    }, extendedImages.map((image, index)=>/*#__PURE__*/ React.createElement("button", {
            key: image.label,
            "data-t": `zoom-modal__navigator-${index}`,
            className: classnames('zoom-modal__navigator-button', {
                _is_active: index === activeIndex
            }),
            onClick: ()=>setSlide(index)
        })
    )), isSmallScreen && rightChevron);
};

export { ZoomModal };
//# sourceMappingURL=ZoomModal.js.map
